import {
    confirmOnboardingSubscriptionResultSchema,
    onboardingSubscriptionOrderSchema,
    type SelectOnboardingSubscriptionInput,
    type ConfirmOnboardingSubscriptionInput,
    type ConfirmOnboardingSubscriptionResult,
    type OnboardingSubscriptionOrder,
} from '@houzy/schemas/partner-cockpit/onboarding';
import {
    queryOptions,
    useMutation,
    useQueryClient,
    type UseMutationResult,
    type UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { isSupportedLanguage } from '../../../i18n';
import { apiRequest, retryQueryUnlessClientError, type ApiContractError, type ApiError } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';

const orderQueryKey = ['onboarding', 'subscription', 'order'] as const;

const path = (suffix = ''): string => partnerCockpitApiPath(`onboarding/subscription${suffix}`);

export function orderQueryOptions(): UseSuspenseQueryOptions<
    OnboardingSubscriptionOrder,
    Error,
    OnboardingSubscriptionOrder,
    typeof orderQueryKey
> {
    return queryOptions({
        queryKey: orderQueryKey,
        queryFn: ({ signal }): Promise<OnboardingSubscriptionOrder> =>
            apiRequest(path('/order'), { signal, responseSchema: onboardingSubscriptionOrderSchema }),
        retry: retryQueryUnlessClientError,
    });
}

export function useSelectPlanMutation(): UseMutationResult<
    OnboardingSubscriptionOrder,
    ApiError | ApiContractError,
    SelectOnboardingSubscriptionInput
> {
    const queryClient = useQueryClient();
    return useMutation<OnboardingSubscriptionOrder, ApiError | ApiContractError, SelectOnboardingSubscriptionInput>({
        mutationFn: (body): Promise<OnboardingSubscriptionOrder> =>
            apiRequest(path(''), { method: 'PUT', body, responseSchema: onboardingSubscriptionOrderSchema }),
        onSuccess: (order) => {
            queryClient.setQueryData(orderQueryKey, order);
        },
    });
}

export function useConfirmOrderMutation(): UseMutationResult<
    ConfirmOnboardingSubscriptionResult,
    ApiError | ApiContractError,
    Omit<ConfirmOnboardingSubscriptionInput, 'language'>
> {
    const { i18n } = useTranslation();

    return useMutation<
        ConfirmOnboardingSubscriptionResult,
        ApiError | ApiContractError,
        Omit<ConfirmOnboardingSubscriptionInput, 'language'>
    >({
        mutationFn: (body): Promise<ConfirmOnboardingSubscriptionResult> =>
            apiRequest(path('/confirm'), {
                method: 'POST',
                body: { ...body, ...(isSupportedLanguage(i18n.language) ? { language: i18n.language } : {}) },
                responseSchema: confirmOnboardingSubscriptionResultSchema,
            }),
    });
}
